import type { AdminMetrics, FaqAdminItem, FaqInput } from '@kk/domain';
import type { VillageWeekPick } from '@kk/domain';
import type {
  PartnerLogoUpload,
  VillageAdminItem,
  VillageContent,
  VillageItemVersion,
  VillagePartner,
  VillagePartnerInput,
} from '@kk/domain';
import type { ClubApplication, ClubApplicationInput } from '@kk/domain';
import type { HttpClient } from '@kk/api-client';

import type {
  ClubBilling,
  ClubBillingReportRow,
  ClubBillingTermsInput,
  AdminAdventure,
  FamilyInvite,
  FamilyInviteInput,
  AdminAdventureInput,
  AdminMission,
  AdminMissionInput,
  Assignment,
  AssignmentInput,
  AssignmentPatch,
  AssignmentPublished,
  DenyCompletionInput,
  GroupInput,
  GroupPatch,
  JoinCodeInput,
  OrgGroupDetail,
  OrgApprovalItem,
  OrgApprovalResult,
  OrgApproveAllResult,
  OrgRedemptionRow,
  OrgRedemptions,
  OrgRoster,
  OrgPatch,
  OrgSummary,
  StaffInvite,
  StaffInviteInput,
  StaffList,
  PublicConfig,
  StaffMe,
  StaffMember,
  StaffRemoved,
  ContentTeam,
  ContentTeamMember,
  ContentTeamRole,
  QuestContentStatus,
  QuestAdventureHistory,
  QuestAuditEntry,
  QuestMissionHistory,
  QuestSyncResult,
  QuestComment,
  QuestEditLock,
  IconCheck,
  QuestIcon,
  QuestIconList,
} from '@/types/wire';

/** The `org`-tag operations of apps/api/openapi.yaml, one method each. Errors are DataClientError (HttpClient). */
export interface StaffDataClient {
  me(): Promise<StaffMe>;
  /** Spanish for 3.0.11: the English · Español switch, saved to the signed-in staff member's own account. */
  updateLanguage(language: 'en' | 'es'): Promise<StaffMe>;
  /** Public config, with no session: `languages` lists Spanish only once Evan opens it. */
  publicConfig(): Promise<PublicConfig>;
  org(orgId: string): Promise<OrgSummary>;
  approvals(orgId: string): Promise<OrgApprovalItem[]>;
  approve(orgId: string, completionId: string): Promise<OrgApprovalResult>;
  deny(orgId: string, completionId: string, input: DenyCompletionInput): Promise<OrgApprovalResult>;
  /** One group only (DECISIONS 21). The Approvals screen approves row by row and does not call it. */
  approveAll(orgId: string, groupId: string): Promise<OrgApproveAllResult>;
  /** Club billing (DECISIONS 90): the club's plan, state and this month; Stripe's checkout and billing pages. */
  billing(orgId: string): Promise<ClubBilling>;
  billingCheckout(orgId: string, plan: 'monthly' | 'yearly'): Promise<{ url: string }>;
  billingPortal(orgId: string): Promise<{ url: string }>;
  /** DECISIONS 92: the owners' Club Billing page: every club's month, and the terms an owner sets. */
  listClubBilling(): Promise<ClubBillingReportRow[]>;
  setClubBillingTerms(orgId: string, input: ClubBillingTermsInput): Promise<ClubBilling>;
  roster(orgId: string): Promise<OrgRoster>;
  redemptions(orgId: string): Promise<OrgRedemptions>;
  lookup(orgId: string, code: string): Promise<OrgRedemptionRow>;
  fulfill(orgId: string, code: string): Promise<OrgRedemptionRow>;
  // O03 (both roles; a group admin sees and targets only their own groups)
  assignments(orgId: string): Promise<Assignment[]>;
  assignment(orgId: string, id: string): Promise<Assignment>;
  createAssignment(orgId: string, input: AssignmentInput): Promise<Assignment>;
  updateAssignment(orgId: string, id: string, patch: AssignmentPatch): Promise<Assignment>;
  publishAssignment(orgId: string, id: string): Promise<AssignmentPublished>;
  // O05 (org admins only — FORBIDDEN for a group admin)
  groups(orgId: string): Promise<OrgGroupDetail[]>;
  createGroup(orgId: string, input: GroupInput): Promise<OrgGroupDetail>;
  updateGroup(orgId: string, id: string, patch: GroupPatch): Promise<OrgGroupDetail>;
  /** `{}` for the defaults (20 uses, 90 days); replaces any active code. */
  generateCode(orgId: string, groupId: string, input?: JoinCodeInput): Promise<OrgGroupDetail>;
  revokeCode(orgId: string, groupId: string): Promise<OrgGroupDetail>;
  staff(orgId: string): Promise<StaffList>;
  inviteStaff(orgId: string, input: StaffInviteInput): Promise<StaffInvite>;
  revokeInvite(orgId: string, inviteId: string): Promise<StaffInvite>;
  /** O08's club header. Every field optional; `null` clears the short form or the badge letters. */
  updateOrg(orgId: string, patch: OrgPatch): Promise<OrgSummary>;

  // O09 · invite families. Resend, never revoke — revoking belongs to the parent, on P21.
  familyInvites(orgId: string): Promise<FamilyInvite[]>;
  inviteFamilies(orgId: string, input: FamilyInviteInput): Promise<FamilyInvite[]>;
  resendFamilyInvite(orgId: string, id: string): Promise<FamilyInvite>;

  // O06 · Quest Jr. content. `/v1/admin/...`, with no orgId anywhere: content belongs to Kiddie Kredit and
  // not to any club, and the absence of an org in these signatures is the scope difference made visible.
  questContent(): Promise<AdminAdventure[]>;
  createAdventure(input: AdminAdventureInput): Promise<AdminAdventure>;
  updateAdventure(id: string, patch: AdminAdventureInput): Promise<AdminAdventure>;
  createMission(input: AdminMissionInput & { adventureId: string }): Promise<AdminMission>;
  updateMission(id: string, patch: AdminMissionInput): Promise<AdminMission>;
  setMissionStatus(id: string, status: QuestContentStatus): Promise<AdminMission>;
  syncQuestSource(): Promise<QuestSyncResult>;
  /** O13 · a mission's versions, newest first, the live pointer, and whether a draft is open. */
  missionVersions(id: string): Promise<QuestMissionHistory>;
  /** O13 · restore is a publish: the version goes live again as a NEW version. */
  restoreVersion(id: string, version: number): Promise<QuestMissionHistory>;
  /** The content audit log for one mission, newest first. */
  questAudit(missionId: string): Promise<QuestAuditEntry[]>;
  /** The editors: an adventure's missions in a new order (every one, exactly once). */
  reorderMissions(adventureId: string, missionIds: string[]): Promise<AdminAdventure>;
  /** Deletes a mission nobody ever saw; anything published is refused (409) and is retired instead. */
  deleteMission(id: string): Promise<void>;
  /** The app's confirm-by-code, from the admin: a code to the signed-in person's own address. */
  requestEmailCode(): Promise<{ requestedAt: string; email: string }>;
  /** The code back: verified, and a waiting content-team invite is joined. */
  confirmEmailCode(code: string): Promise<{ emailVerified: true; confirmedAt: string }>;
  /** Part 4: the live content as the repo's files (the rail's Export JSON). */
  exportContent(): Promise<{
    exportedAt: string;
    files: Array<{ path: string; text: string }>;
    notCarried: string[];
  }>;
  /** O14 · the icon library: every icon, bundled and uploaded, with where each is used; the manifest's version. */
  iconLibrary(): Promise<QuestIconList>;
  /** The validator and clean-up on a file, saving nothing (O14's checks, verdict and previews). */
  checkIcon(svg: string): Promise<IconCheck>;
  /** Saves a checked icon as a DRAFT under its key: it reaches phones only once another Publisher publishes it. */
  uploadIcon(key: string, svg: string): Promise<QuestIcon>;
  publishIcon(key: string): Promise<QuestIcon>;
  retireIcon(key: string): Promise<QuestIcon>;
  /** The Team page: the content team and the caller's role. */
  contentTeam(): Promise<ContentTeam>;
  /** Village Square, the team's side (P35's notes). */
  villageItems(): Promise<VillageAdminItem[]>;
  /** The admin's metrics (counts only, owners only). */
  metrics(): Promise<AdminMetrics>;
  /** Help's FAQ (3.0.10). */
  faq(): Promise<FaqAdminItem[]>;
  createFaqItem(input: FaqInput): Promise<FaqAdminItem>;
  updateFaqItem(id: string, input: FaqInput): Promise<FaqAdminItem>;
  faqItemAction(id: string, action: 'publish' | 'unpublish'): Promise<FaqAdminItem>;
  deleteFaqItem(id: string): Promise<void>;
  /** P35d's weekly pick (28 Sep): the line-up, and one week set or cleared. */
  villageWeekPicks(): Promise<VillageWeekPick[]>;
  setVillageWeekPick(week: string, itemId: string | null): Promise<VillageWeekPick>;
  createVillageItem(content: VillageContent): Promise<VillageAdminItem>;
  updateVillageItem(id: string, content: VillageContent): Promise<VillageAdminItem>;
  villageItemAction(
    id: string,
    action: 'review' | 'partner-approval' | 'publish' | 'take-down',
  ): Promise<VillageAdminItem>;
  villageItemVersions(id: string): Promise<VillageItemVersion[]>;
  restoreVillageItemVersion(id: string, version: number): Promise<VillageAdminItem>;
  discardVillageDraft(id: string): Promise<VillageAdminItem>;
  villagePartners(): Promise<VillagePartner[]>;
  createVillagePartner(input: VillagePartnerInput): Promise<VillagePartner>;
  updateVillagePartner(id: string, input: Partial<VillagePartnerInput>): Promise<VillagePartner>;
  approveVillagePartner(id: string): Promise<VillagePartner>;
  uploadVillagePartnerLogo(id: string, upload: PartnerLogoUpload): Promise<VillagePartner>;
  approveVillagePartnerLogo(id: string): Promise<VillagePartner>;
  /** O18: the public form, with no session. */
  submitClubApplication(input: ClubApplicationInput): Promise<{ received: true }>;
  /** O18: the admin's Applications page, owners only. */
  clubApplications(): Promise<ClubApplication[]>;
  approveClubApplication(id: string): Promise<ClubApplication>;
  declineClubApplication(id: string): Promise<ClubApplication>;
  addTeamMember(input: {
    email: string;
    role: ContentTeamRole;
    endsOn?: string;
  }): Promise<ContentTeamMember>;
  /** A role, an end date, or both (`endsOn: null` clears it). */
  setTeamRole(
    id: string,
    change: { role?: ContentTeamRole; endsOn?: string | null },
  ): Promise<ContentTeamMember>;
  removeTeamMember(id: string): Promise<void>;
  /** Throws a live mission's draft away (O11 "Discard draft"). */
  discardMissionDraft(id: string): Promise<AdminMission>;
  // working together (2026-09-26)
  comments(missionId: string): Promise<QuestComment[]>;
  addComment(missionId: string, input: { anchor: string; text: string }): Promise<QuestComment>;
  resolveComment(id: string): Promise<QuestComment>;
  /** Takes or keeps the editing lock; `takeOver` is an owner's. */
  lockMission(id: string, takeOver?: boolean): Promise<QuestEditLock>;
  unlockMission(id: string): Promise<void>;
  /** The first Publisher to open a waiting draft takes it. */
  claimReview(id: string): Promise<AdminMission>;
  /** O13 "Copy into the draft": an old version becomes the working copy, to fix and publish. */
  copyVersionToDraft(id: string, version: number): Promise<AdminMission>;
  /** An adventure's draft goes live as its next version (Evan, 2026-09-26). */
  publishAdventure(id: string): Promise<AdminAdventure>;
  discardAdventureDraft(id: string): Promise<AdminAdventure>;
  adventureVersions(id: string): Promise<QuestAdventureHistory>;
  restoreAdventureVersion(id: string, version: number): Promise<QuestAdventureHistory>;
  /**
   * O08's staff list. Removes a PERSON, immediately — their next request fails — and leaves what they
   * approved alone. The last org admin is refused (409), which is why that row draws an em dash instead of
   * a Remove somebody could press.
   */
  removeStaff(orgId: string, id: string): Promise<StaffRemoved>;
  /** The same removal, done to yourself: no id, any role, and the last org admin is refused here too. */
  leaveStaffOrg(orgId: string): Promise<StaffRemoved>;
  /** How you are named to families — your own row only. `null` clears it back to the account name. */
  updateStaffProfile(orgId: string, displayName: string | null): Promise<StaffMember>;
}

const org = (orgId: string) => `/v1/org/${encodeURIComponent(orgId)}`;

export class StaffApiClient implements StaffDataClient {
  constructor(private readonly http: HttpClient) {}

  me(): Promise<StaffMe> {
    return this.http.request('GET', '/v1/org/me');
  }
  updateLanguage(language: 'en' | 'es'): Promise<StaffMe> {
    return this.http.request('PATCH', '/v1/org/me/language', { body: { language } });
  }
  publicConfig(): Promise<PublicConfig> {
    return this.http.request('GET', '/v1/public/config?client=portal', { auth: false });
  }
  org(orgId: string): Promise<OrgSummary> {
    return this.http.request('GET', org(orgId));
  }
  approvals(orgId: string): Promise<OrgApprovalItem[]> {
    return this.http.request('GET', `${org(orgId)}/approvals`);
  }
  approve(orgId: string, completionId: string): Promise<OrgApprovalResult> {
    return this.http.request(
      'POST',
      `${org(orgId)}/approvals/${encodeURIComponent(completionId)}/approve`,
    );
  }
  deny(
    orgId: string,
    completionId: string,
    input: DenyCompletionInput,
  ): Promise<OrgApprovalResult> {
    return this.http.request(
      'POST',
      `${org(orgId)}/approvals/${encodeURIComponent(completionId)}/deny`,
      { body: input },
    );
  }
  approveAll(orgId: string, groupId: string): Promise<OrgApproveAllResult> {
    return this.http.request('POST', `${org(orgId)}/approvals/approve-all`, { body: { groupId } });
  }
  billing(orgId: string): Promise<ClubBilling> {
    return this.http.request('GET', `${org(orgId)}/billing`);
  }
  billingCheckout(orgId: string, plan: 'monthly' | 'yearly'): Promise<{ url: string }> {
    return this.http.request('POST', `${org(orgId)}/billing/checkout`, { body: { plan } });
  }
  billingPortal(orgId: string): Promise<{ url: string }> {
    return this.http.request('POST', `${org(orgId)}/billing/portal`);
  }
  listClubBilling(): Promise<ClubBillingReportRow[]> {
    return this.http.request('GET', '/v1/admin/club-billing');
  }
  setClubBillingTerms(orgId: string, input: ClubBillingTermsInput): Promise<ClubBilling> {
    return this.http.request('PATCH', `/v1/admin/club-billing/${encodeURIComponent(orgId)}`, {
      body: input,
    });
  }
  roster(orgId: string): Promise<OrgRoster> {
    return this.http.request('GET', `${org(orgId)}/roster`);
  }
  redemptions(orgId: string): Promise<OrgRedemptions> {
    return this.http.request('GET', `${org(orgId)}/redemptions`);
  }
  lookup(orgId: string, code: string): Promise<OrgRedemptionRow> {
    return this.http.request('GET', `${org(orgId)}/redemptions/${encodeURIComponent(code)}`);
  }
  fulfill(orgId: string, code: string): Promise<OrgRedemptionRow> {
    return this.http.request('POST', `${org(orgId)}/redemptions/fulfill`, { body: { code } });
  }

  assignments(orgId: string): Promise<Assignment[]> {
    return this.http.request('GET', `${org(orgId)}/assignments`);
  }
  assignment(orgId: string, id: string): Promise<Assignment> {
    return this.http.request('GET', `${org(orgId)}/assignments/${encodeURIComponent(id)}`);
  }
  createAssignment(orgId: string, input: AssignmentInput): Promise<Assignment> {
    return this.http.request('POST', `${org(orgId)}/assignments`, { body: input });
  }
  updateAssignment(orgId: string, id: string, patch: AssignmentPatch): Promise<Assignment> {
    return this.http.request('PATCH', `${org(orgId)}/assignments/${encodeURIComponent(id)}`, {
      body: patch,
    });
  }
  publishAssignment(orgId: string, id: string): Promise<AssignmentPublished> {
    return this.http.request('POST', `${org(orgId)}/assignments/${encodeURIComponent(id)}/publish`);
  }

  groups(orgId: string): Promise<OrgGroupDetail[]> {
    return this.http.request('GET', `${org(orgId)}/groups`);
  }
  createGroup(orgId: string, input: GroupInput): Promise<OrgGroupDetail> {
    return this.http.request('POST', `${org(orgId)}/groups`, { body: input });
  }
  updateGroup(orgId: string, id: string, patch: GroupPatch): Promise<OrgGroupDetail> {
    return this.http.request('PATCH', `${org(orgId)}/groups/${encodeURIComponent(id)}`, {
      body: patch,
    });
  }
  generateCode(orgId: string, groupId: string, input: JoinCodeInput = {}): Promise<OrgGroupDetail> {
    return this.http.request('POST', `${org(orgId)}/groups/${encodeURIComponent(groupId)}/code`, {
      body: input,
    });
  }
  revokeCode(orgId: string, groupId: string): Promise<OrgGroupDetail> {
    return this.http.request(
      'POST',
      `${org(orgId)}/groups/${encodeURIComponent(groupId)}/code/revoke`,
    );
  }
  staff(orgId: string): Promise<StaffList> {
    return this.http.request('GET', `${org(orgId)}/staff`);
  }
  inviteStaff(orgId: string, input: StaffInviteInput): Promise<StaffInvite> {
    return this.http.request('POST', `${org(orgId)}/staff/invites`, { body: input });
  }
  revokeInvite(orgId: string, inviteId: string): Promise<StaffInvite> {
    return this.http.request(
      'POST',
      `${org(orgId)}/staff/invites/${encodeURIComponent(inviteId)}/revoke`,
    );
  }
  updateOrg(orgId: string, patch: OrgPatch): Promise<OrgSummary> {
    return this.http.request('PATCH', org(orgId), { body: patch });
  }

  familyInvites(orgId: string): Promise<FamilyInvite[]> {
    return this.http.request('GET', `${org(orgId)}/family-invites`);
  }
  inviteFamilies(orgId: string, input: FamilyInviteInput): Promise<FamilyInvite[]> {
    return this.http.request('POST', `${org(orgId)}/family-invites`, { body: input });
  }
  resendFamilyInvite(orgId: string, id: string): Promise<FamilyInvite> {
    return this.http.request(
      'POST',
      `${org(orgId)}/family-invites/${encodeURIComponent(id)}/resend`,
    );
  }

  questContent(): Promise<AdminAdventure[]> {
    return this.http.request('GET', '/v1/admin/quest/adventures');
  }
  createAdventure(input: AdminAdventureInput): Promise<AdminAdventure> {
    return this.http.request('POST', '/v1/admin/quest/adventures', { body: input });
  }
  updateAdventure(id: string, patch: AdminAdventureInput): Promise<AdminAdventure> {
    return this.http.request('PATCH', `/v1/admin/quest/adventures/${encodeURIComponent(id)}`, {
      body: patch,
    });
  }
  createMission(input: AdminMissionInput & { adventureId: string }): Promise<AdminMission> {
    return this.http.request('POST', '/v1/admin/quest/missions', { body: input });
  }
  updateMission(id: string, patch: AdminMissionInput): Promise<AdminMission> {
    return this.http.request('PATCH', `/v1/admin/quest/missions/${encodeURIComponent(id)}`, {
      body: patch,
    });
  }
  setMissionStatus(id: string, status: QuestContentStatus): Promise<AdminMission> {
    return this.http.request('POST', `/v1/admin/quest/missions/${encodeURIComponent(id)}/status`, {
      body: { status },
    });
  }
  syncQuestSource(): Promise<QuestSyncResult> {
    return this.http.request('POST', '/v1/admin/quest/sync');
  }
  missionVersions(id: string): Promise<QuestMissionHistory> {
    return this.http.request('GET', `/v1/admin/quest/missions/${encodeURIComponent(id)}/versions`);
  }
  restoreVersion(id: string, version: number): Promise<QuestMissionHistory> {
    return this.http.request(
      'POST',
      `/v1/admin/quest/missions/${encodeURIComponent(id)}/versions/${version}/restore`,
    );
  }
  questAudit(missionId: string): Promise<QuestAuditEntry[]> {
    return this.http.request('GET', '/v1/admin/quest/audit', { query: { missionId, limit: '12' } });
  }
  reorderMissions(adventureId: string, missionIds: string[]): Promise<AdminAdventure> {
    return this.http.request(
      'PUT',
      `/v1/admin/quest/adventures/${encodeURIComponent(adventureId)}/order`,
      {
        body: { missionIds },
      },
    );
  }
  async deleteMission(id: string): Promise<void> {
    await this.http.request('DELETE', `/v1/admin/quest/missions/${encodeURIComponent(id)}`);
  }
  requestEmailCode(): Promise<{ requestedAt: string; email: string }> {
    return this.http.request('POST', '/v1/me/email/code');
  }
  confirmEmailCode(code: string): Promise<{ emailVerified: true; confirmedAt: string }> {
    return this.http.request('POST', '/v1/me/email/confirm', { body: { code } });
  }
  exportContent(): Promise<{
    exportedAt: string;
    files: Array<{ path: string; text: string }>;
    notCarried: string[];
  }> {
    return this.http.request('GET', '/v1/admin/quest/export');
  }
  iconLibrary(): Promise<QuestIconList> {
    return this.http.request('GET', '/v1/admin/quest/icons');
  }
  checkIcon(svg: string): Promise<IconCheck> {
    return this.http.request('POST', '/v1/admin/quest/icons/check', { body: { svg } });
  }
  uploadIcon(key: string, svg: string): Promise<QuestIcon> {
    return this.http.request('POST', '/v1/admin/quest/icons', { body: { key, svg } });
  }
  publishIcon(key: string): Promise<QuestIcon> {
    return this.http.request('POST', `/v1/admin/quest/icons/${encodeURIComponent(key)}/publish`);
  }
  retireIcon(key: string): Promise<QuestIcon> {
    return this.http.request('POST', `/v1/admin/quest/icons/${encodeURIComponent(key)}/retire`);
  }
  submitClubApplication(input: ClubApplicationInput): Promise<{ received: true }> {
    return this.http.request('POST', '/v1/club-applications', { auth: false, body: input });
  }
  clubApplications(): Promise<ClubApplication[]> {
    return this.http.request('GET', '/v1/admin/club-applications');
  }
  approveClubApplication(id: string): Promise<ClubApplication> {
    return this.http.request(
      'POST',
      `/v1/admin/club-applications/${encodeURIComponent(id)}/approve`,
      { body: {} },
    );
  }
  declineClubApplication(id: string): Promise<ClubApplication> {
    return this.http.request(
      'POST',
      `/v1/admin/club-applications/${encodeURIComponent(id)}/decline`,
    );
  }
  villageItems(): Promise<VillageAdminItem[]> {
    return this.http.request('GET', '/v1/admin/village-square/items');
  }
  metrics(): Promise<AdminMetrics> {
    return this.http.request('GET', '/v1/admin/metrics');
  }
  faq(): Promise<FaqAdminItem[]> {
    return this.http.request('GET', '/v1/admin/faq');
  }
  createFaqItem(input: FaqInput): Promise<FaqAdminItem> {
    return this.http.request('POST', '/v1/admin/faq', { body: input });
  }
  updateFaqItem(id: string, input: FaqInput): Promise<FaqAdminItem> {
    return this.http.request('PUT', `/v1/admin/faq/${encodeURIComponent(id)}`, { body: input });
  }
  faqItemAction(id: string, action: 'publish' | 'unpublish'): Promise<FaqAdminItem> {
    return this.http.request('POST', `/v1/admin/faq/${encodeURIComponent(id)}/${action}`);
  }
  async deleteFaqItem(id: string): Promise<void> {
    await this.http.request('DELETE', `/v1/admin/faq/${encodeURIComponent(id)}`);
  }
  villageWeekPicks(): Promise<VillageWeekPick[]> {
    return this.http.request('GET', '/v1/admin/village-square/picks');
  }
  setVillageWeekPick(week: string, itemId: string | null): Promise<VillageWeekPick> {
    return this.http.request('PUT', `/v1/admin/village-square/picks/${encodeURIComponent(week)}`, {
      body: { itemId },
    });
  }
  createVillageItem(content: VillageContent): Promise<VillageAdminItem> {
    return this.http.request('POST', '/v1/admin/village-square/items', { body: content });
  }
  updateVillageItem(id: string, content: VillageContent): Promise<VillageAdminItem> {
    return this.http.request('PUT', `/v1/admin/village-square/items/${encodeURIComponent(id)}`, {
      body: content,
    });
  }
  villageItemAction(
    id: string,
    action: 'review' | 'partner-approval' | 'publish' | 'take-down',
  ): Promise<VillageAdminItem> {
    return this.http.request(
      'POST',
      `/v1/admin/village-square/items/${encodeURIComponent(id)}/${action}`,
    );
  }
  villageItemVersions(id: string): Promise<VillageItemVersion[]> {
    return this.http.request(
      'GET',
      `/v1/admin/village-square/items/${encodeURIComponent(id)}/versions`,
    );
  }
  restoreVillageItemVersion(id: string, version: number): Promise<VillageAdminItem> {
    return this.http.request(
      'POST',
      `/v1/admin/village-square/items/${encodeURIComponent(id)}/versions/${version}/restore`,
    );
  }
  discardVillageDraft(id: string): Promise<VillageAdminItem> {
    return this.http.request(
      'DELETE',
      `/v1/admin/village-square/items/${encodeURIComponent(id)}/draft`,
    );
  }
  villagePartners(): Promise<VillagePartner[]> {
    return this.http.request('GET', '/v1/admin/village-square/partners');
  }
  createVillagePartner(input: VillagePartnerInput): Promise<VillagePartner> {
    return this.http.request('POST', '/v1/admin/village-square/partners', { body: input });
  }
  updateVillagePartner(id: string, input: Partial<VillagePartnerInput>): Promise<VillagePartner> {
    return this.http.request(
      'PATCH',
      `/v1/admin/village-square/partners/${encodeURIComponent(id)}`,
      { body: input },
    );
  }
  uploadVillagePartnerLogo(id: string, upload: PartnerLogoUpload): Promise<VillagePartner> {
    return this.http.request(
      'POST',
      `/v1/admin/village-square/partners/${encodeURIComponent(id)}/logo`,
      { body: upload },
    );
  }
  approveVillagePartnerLogo(id: string): Promise<VillagePartner> {
    return this.http.request(
      'POST',
      `/v1/admin/village-square/partners/${encodeURIComponent(id)}/logo/approve`,
    );
  }
  approveVillagePartner(id: string): Promise<VillagePartner> {
    return this.http.request(
      'POST',
      `/v1/admin/village-square/partners/${encodeURIComponent(id)}/approve`,
    );
  }
  contentTeam(): Promise<ContentTeam> {
    return this.http.request('GET', '/v1/admin/team');
  }
  addTeamMember(input: {
    email: string;
    role: ContentTeamRole;
    endsOn?: string;
  }): Promise<ContentTeamMember> {
    return this.http.request('POST', '/v1/admin/team', { body: input });
  }
  setTeamRole(
    id: string,
    change: { role?: ContentTeamRole; endsOn?: string | null },
  ): Promise<ContentTeamMember> {
    return this.http.request('PATCH', `/v1/admin/team/${encodeURIComponent(id)}`, { body: change });
  }
  async removeTeamMember(id: string): Promise<void> {
    await this.http.request('DELETE', `/v1/admin/team/${encodeURIComponent(id)}`);
  }
  discardMissionDraft(id: string): Promise<AdminMission> {
    return this.http.request('DELETE', `/v1/admin/quest/missions/${encodeURIComponent(id)}/draft`);
  }
  comments(missionId: string): Promise<QuestComment[]> {
    return this.http.request(
      'GET',
      `/v1/admin/quest/missions/${encodeURIComponent(missionId)}/comments`,
    );
  }
  addComment(missionId: string, input: { anchor: string; text: string }): Promise<QuestComment> {
    return this.http.request(
      'POST',
      `/v1/admin/quest/missions/${encodeURIComponent(missionId)}/comments`,
      { body: input },
    );
  }
  resolveComment(id: string): Promise<QuestComment> {
    return this.http.request('POST', `/v1/admin/quest/comments/${encodeURIComponent(id)}/resolve`);
  }
  lockMission(id: string, takeOver = false): Promise<QuestEditLock> {
    return this.http.request('POST', `/v1/admin/quest/missions/${encodeURIComponent(id)}/lock`, {
      body: takeOver ? { takeOver: true } : {},
    });
  }
  async unlockMission(id: string): Promise<void> {
    await this.http.request('DELETE', `/v1/admin/quest/missions/${encodeURIComponent(id)}/lock`);
  }
  claimReview(id: string): Promise<AdminMission> {
    return this.http.request('POST', `/v1/admin/quest/missions/${encodeURIComponent(id)}/claim`);
  }
  copyVersionToDraft(id: string, version: number): Promise<AdminMission> {
    return this.http.request(
      'POST',
      `/v1/admin/quest/missions/${encodeURIComponent(id)}/versions/${version}/copy-to-draft`,
    );
  }
  publishAdventure(id: string): Promise<AdminAdventure> {
    return this.http.request(
      'POST',
      `/v1/admin/quest/adventures/${encodeURIComponent(id)}/publish`,
    );
  }
  discardAdventureDraft(id: string): Promise<AdminAdventure> {
    return this.http.request(
      'DELETE',
      `/v1/admin/quest/adventures/${encodeURIComponent(id)}/draft`,
    );
  }
  adventureVersions(id: string): Promise<QuestAdventureHistory> {
    return this.http.request(
      'GET',
      `/v1/admin/quest/adventures/${encodeURIComponent(id)}/versions`,
    );
  }
  restoreAdventureVersion(id: string, version: number): Promise<QuestAdventureHistory> {
    return this.http.request(
      'POST',
      `/v1/admin/quest/adventures/${encodeURIComponent(id)}/versions/${version}/restore`,
    );
  }
  removeStaff(orgId: string, id: string): Promise<StaffRemoved> {
    return this.http.request('POST', `${org(orgId)}/staff/${encodeURIComponent(id)}/remove`);
  }
  leaveStaffOrg(orgId: string): Promise<StaffRemoved> {
    return this.http.request('POST', `${org(orgId)}/staff/leave`);
  }
  updateStaffProfile(orgId: string, displayName: string | null): Promise<StaffMember> {
    return this.http.request('PATCH', `${org(orgId)}/staff/me`, { body: { displayName } });
  }
}
